import mgr from '@core/services/SecurityService';
import {useResetStore} from '@core/utils/resetStores';
import AuthService from '@core/services/AuthService';
import {sanitizeInternalRedirect} from '@core/utils/sanitizeInternalRedirect';
import type {ComponentPublicInstance} from 'vue';
import type {RouteRecordRaw} from 'vue-router';

const isDevEnv = import.meta.env.DEV;

const Issues = () => import('@core/views/issues/IssueDetail.vue');
const Releases = () => import('@core/views/releases/ReleaseList.vue');
const GeneralSettings = () =>
  import('@core/views/settings/GeneralSettings.vue');
const ThemeSettings = () => import('@core/views/settings/ThemeSettings.vue');
const UploadSettings = () => import('@core/views/settings/UploadSettings.vue');
const EmailTemplatesDetails = () =>
  import('@core/views/email-templates/EmailTemplateDetail.vue');
const OnboardingPage = () =>
  import('@core/views/onboarding/SetupEnvironments.vue');
const NoEnvironmentAccess = () => import('@core/views/NoEnvironmentAccess.vue');

const routes: RouteRecordRaw[] = [
  {
    path: '/releasenotes',
    name: 'releasenotes',
    component: Releases,
    meta: {core: true},
  },
  {
    path: '/issues',
    name: 'issues',
    component: Issues,
    meta: {core: true},
  },
  {
    path: '/settings',
    redirect: {name: 'settings_general'},
  },
  {
    path: '/settings/general',
    name: 'settings_general',
    component: GeneralSettings,
    meta: {core: true, admin: true},
  },
  {
    path: '/settings/theme',
    name: 'settings_theme',
    component: ThemeSettings,
    meta: {core: true, admin: true},
  },
  {
    path: '/settings/uploads',
    name: 'settings_uploads',
    component: UploadSettings,
    meta: {core: true, admin: true},
  },
  {
    path: '/settings/emailTemplates/:id',
    name: 'settings_emailTemplates_details',
    component: EmailTemplatesDetails,
    meta: {core: true, admin: true},
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: OnboardingPage,
    meta: {
      core: true,
      onboarding: true,
      title: 'internalDevPortal.onboarding.createEnvironment',
    },
  },
  {
    path: '/no-environment-access',
    name: 'noEnvironmentAccess',
    component: NoEnvironmentAccess,
    meta: {
      core: true,
      noEnvironmentAccess: true,
      title: 'No accessible environments',
    },
  },
  {
    path: '/login',
    meta: {public: true},
    component: {
      render: () => null,
      async mounted(this: ComponentPublicInstance) {
        const safe = sanitizeInternalRedirect(this.$route.query.redirect);
        await AuthService.login(safe ?? undefined);
        if (AuthService.isAuthenticated()) {
          globalThis.location.href = safe ?? '/';
        }
      },
    },
  },
  {
    path: '/logout',
    meta: {public: true},
    component: {
      render: () => null,
      async mounted() {
        await AuthService.logout();
        globalThis.location.href = '/';
      },
    },
  },
  {
    path: '/callback_logout',
    component: {
      render: () => null,
      mounted() {
        const localSettings = localStorage.getItem('SettingsStore');

        localStorage.clear();
        useResetStore().all();

        // Restore Language and Theme settings
        if (localSettings) localStorage.setItem('SettingsStore', localSettings);

        // Redirect to the home page
        globalThis.location.href = '/';
      },
    },
  },
  {
    path: '/callback_silent',
    component: {
      render: () => null,
      async mounted() {
        try {
          await mgr.signinSilentCallback();
        } catch (error) {
          console.error('signinSilentCallback failed', error);
        }
      },
    },
  },
];

if (isDevEnv) {
  routes.push({
    path: '/force_login_check',
    component: {
      render: () => null,
      async mounted() {
        await AuthService.login();
        if (AuthService.isAuthenticated()) {
          globalThis.location.href = '/';
        }
      },
    },
  });
}

export {routes};
